import { BdHelpLink } from './help.model';

/**
 * All help links, baked in at build time.
 *
 * Keyed by the same strings that `<bd-help-button key="…">` uses.
 * Adding a new entry: add one property here — no JSON files, no HTTP requests.
 */
export const HELP_LINKS: Record<string, BdHelpLink> = {
  // --- analytics: business-case ---
  ays_business_case_clinic: {
    key: 'ays_business_case_clinic',
    displayName: 'Auswahl Klinik',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23494863/Business+Case#Klinik',
  },
  ays_business_case_clinic_drgs: {
    key: 'ays_business_case_clinic_drgs',
    displayName: 'Klinik DRGs',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23494863/Business+Case#Klinik-DRGs',
  },
  ays_business_case_potenzielle_drgs: {
    key: 'ays_business_case_potenzielle_drgs',
    displayName: 'Potenzielle DRGs',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23494863/Business+Case#Potenzielle-DRG',
  },
  ays_business_case_investitions_parameter: {
    key: 'ays_business_case_investitions_parameter',
    displayName: 'Investitions-Parameter',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23494863/Business+Case#Investitions-Parameter',
  },
  ays_business_case_ergebnis: {
    key: 'ays_business_case_ergebnis',
    displayName: 'Ergebnis',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23494863/Business+Case#Ergebnis',
  },

  // --- analytics: clinic ---
  ays_clinic_browser: {
    key: 'ays_clinic_browser',
    displayName: 'Klinik-Browser',
    link: 'https://bindoc.atlassian.net/wiki/x/X4BlAQ',
  },

  // --- analytics: clinics-filter ---
  ays_clinics_filter: {
    key: 'ays_clinics_filter',
    displayName: 'Klinik-Filter',
    link: 'https://bindoc.atlassian.net/wiki/x/LIBWAQ',
  },
  'ays_clinics_filter--clinic_level': {
    key: 'ays_clinics_filter--clinic_level',
    displayName: 'Klinik-Filter: Versorgungslevel',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/37847055/Klinik-Filter+Elemente#Versorgungslevel',
  },

  // --- analytics: code-browser ---
  ays_code_browser_drg: {
    key: 'ays_code_browser_drg',
    displayName: 'DRG-Browser',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23429232/Code-Browser#DRG-Browser',
  },
  ays_code_browser_icd: {
    key: 'ays_code_browser_icd',
    displayName: 'ICD-Browser',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23429232/Code-Browser#ICD-und-OPS-Browser',
  },
  ays_code_browser_ops: {
    key: 'ays_code_browser_ops',
    displayName: 'OPS-Browser',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23429232/Code-Browser#ICD-und-OPS-Browser',
  },

  // --- analytics: code-spectrum ---
  ays_code_spectrum: {
    key: 'ays_code_spectrum',
    displayName: 'Ziffer-Spektra',
    link: 'https://bindoc.atlassian.net/wiki/x/AoAkAg',
  },

  // --- analytics: competitors-analysis ---
  ays_competitor_analysis: {
    key: 'ays_competitor_analysis',
    displayName: 'Wettbewerbsanalyse',
    link: 'https://bindoc.atlassian.net/wiki/x/e4BmAQ',
  },

  // --- analytics: cube ---
  ays_cube: {
    key: 'ays_cube',
    displayName: 'Cube',
    link: 'https://bindoc.atlassian.net/wiki/x/AYBXL',
  },

  // --- analytics: demand-analysis ---
  ays_demand_analysis: {
    key: 'ays_demand_analysis',
    displayName: 'Versorgungsanalyse',
    link: 'https://bindoc.atlassian.net/wiki/x/AgBiB',
  },

  // --- analytics: drg-benchmarking ---
  ays_drg_benchmarking: {
    key: 'ays_drg_benchmarking',
    displayName: 'DRG-Benchmarking',
    link: 'https://bindoc.atlassian.net/wiki/x/X4BmAQ',
  },

  // --- analytics: geographic-analysis ---
  ays_geographic_analysis: {
    key: 'ays_geographic_analysis',
    displayName: 'Geo-Analyse',
    link: 'https://bindoc.atlassian.net/wiki/x/jIBlAQ',
  },

  // --- analytics: home ---
  ays_home: {
    key: 'ays_home',
    displayName: 'Dashboard',
    link: 'https://bindoc.atlassian.net/wiki/x/BQBIAQ',
  },

  // --- analytics: maps ---
  ays_maps_geographic_analysis: {
    key: 'ays_maps_geographic_analysis',
    displayName: 'Geo-Analyse',
    link: 'https://bindoc.atlassian.net/wiki/x/jIBlAQ',
  },

  // --- analytics: market ---
  ays_market: {
    key: 'ays_market',
    displayName: 'Märkte',
    link: 'https://bindoc.atlassian.net/wiki/x/LwBJAQ',
  },

  /*
   * --- analytics: Kennzahlen-Benchmarking ---
   *
   * Survives the removal of the `/operating-numbers` page for the same reason
   * the `OPERATING_NUMBERS` feature id, the licensed name and the
   * `utm_source=kennzahlen-benchmarking` campaign parameter do (BDA-5849): the
   * TOOL still exists, at `/clinics-cube`, under this exact name. The page was
   * deleted, the entitlement was not — and `BD_OPERATING_NUMBERS_FEATURE`
   * names this key, so removing it leaves that tool as the only one in the
   * catalogue whose help button opens nothing.
   *
   * The Confluence article behind it still describes the old page and wants a
   * rewrite for the cube; that is a content job, not a reason to unwire the
   * button.
   */
  ays_operating_numbers_drg_benchmarking: {
    key: 'ays_operating_numbers_drg_benchmarking',
    displayName: 'Kennzahlen-Benchmarking',
    link: 'https://bindoc.atlassian.net/wiki/x/9YBHAQ',
  },

  // --- analytics: potential ---
  ays_potential: {
    key: 'ays_potential',
    displayName: 'Potenzialanalyse',
    link: 'https://bindoc.atlassian.net/wiki/x/14BpAQ',
  },

  // --- analytics: projection ---
  ays_projection: {
    key: 'ays_projection',
    displayName: 'Prognose Analyse Tool',
    link: 'https://bindoc.atlassian.net/wiki/x/u4BpAQ',
  },

  // --- analytics: scenario-analysis ---
  ays_scenario_analysis: {
    key: 'ays_scenario_analysis',
    displayName: 'Szenarioanalyse',
    link: 'https://bindoc.atlassian.net/wiki/x/qIBlAQ',
  },

  // --- analytics: virtual-benchmarking-group ---
  ays_virtual_benchmarking_group: {
    key: 'ays_virtual_benchmarking_group',
    displayName: 'Virtuelle Benchmarking-Gruppen',
    link: 'https://bindoc.atlassian.net/wiki/x/XoBpAQ',
  },

  // --- analytics: virtual-clinic ---
  ays_virtual_clinic: {
    key: 'ays_virtual_clinic',
    displayName: 'Virtuelle Klinik',
    link: 'https://bindoc.atlassian.net/wiki/x/AoBmAQ',
  },
  ays_virtual_clinic_import: {
    key: 'ays_virtual_clinic_import',
    displayName: 'Leistungs- und Strukturdaten Import',
    link: 'https://help.bindoc.de/dokumentation-analytics-virtuelle-klinik#import',
  },

  // --- analytics: virtual-clinic-referral ---
  ays_virtual_clinic_referral: {
    key: 'ays_virtual_clinic_referral',
    displayName: 'Einweiseranalyse',
    link: 'https://bindoc.atlassian.net/wiki/spaces/HP/pages/23494807/Einweiseranalyse',
  },

  // --- hartmann: opportunity-costs (DE) ---
  'htm-opportunity-costs-selection': {
    key: 'htm-opportunity-costs-selection',
    displayName: 'Geographische Auswahl',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention#einstieg',
  },
  'htm-opportunity-costs-dashboard-fully-inpatient-cases': {
    key: 'htm-opportunity-costs-dashboard-fully-inpatient-cases',
    displayName: 'Fallzahlen',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/fallzahlen',
  },
  'htm-opportunity-costs-clinic-cost-savings': {
    key: 'htm-opportunity-costs-clinic-cost-savings',
    displayName: 'Kosteneinsparung',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/kosteneinsparung',
  },
  'htm-opportunity-costs-clinic-opportunity-costs': {
    key: 'htm-opportunity-costs-clinic-opportunity-costs',
    displayName: 'Opportunitätskosten',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/opportunitaetskosten',
  },
  'htm-opportunity-costs-clinic-parameters': {
    key: 'htm-opportunity-costs-clinic-parameters',
    displayName: 'Parameter',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/parameter',
  },
  'htm-opportunity-costs-clinic-parameters-sonstige-sepsis': {
    key: 'htm-opportunity-costs-clinic-parameters-sonstige-sepsis',
    displayName: 'Sonstige Sepsis',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/parameter#sonstige-sepsis',
  },
  'htm-opportunity-costs-clinic-parameters-infektion-nach-eingriff': {
    key: 'htm-opportunity-costs-clinic-parameters-infektion-nach-eingriff',
    displayName: 'Infektion nach einem Eingriff',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/parameter#infektion-nach-eingriff',
  },
  'htm-opportunity-costs-clinic-parameters-clostridium-difficile': {
    key: 'htm-opportunity-costs-clinic-parameters-clostridium-difficile',
    displayName: 'Enterokolitis durch Clostridioides difficile',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/parameter#clostridium-difficile',
  },
  'htm-opportunity-costs-clinic-parameters-harnwegsinfektion': {
    key: 'htm-opportunity-costs-clinic-parameters-harnwegsinfektion',
    displayName: 'Harnwegsinfektion',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/parameter#harnwegsinfektion',
  },
  'htm-opportunity-costs-clinic-parameters-infektion-untere-atemwege': {
    key: 'htm-opportunity-costs-clinic-parameters-infektion-untere-atemwege',
    displayName: 'Akute Infektion der unteren Atemwege',
    link: 'https://hartmann-help.bindoc.de/dokumentation/infection-prevention/parameter#infektion-untere-atemwege',
  },

  // --- hartmann: opportunity-costs (CH) ---
  // CH keys are suffixed with '-ch' to avoid collision with DE keys above.
  'htm-opportunity-costs-ch-selection': {
    key: 'htm-opportunity-costs-ch-selection',
    displayName: 'Geographische Auswahl',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention#einstieg',
  },
  'htm-opportunity-costs-ch-dashboard-fully-inpatient-cases': {
    key: 'htm-opportunity-costs-ch-dashboard-fully-inpatient-cases',
    displayName: 'Fallzahlen',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/fallzahlen',
  },
  'htm-opportunity-costs-ch-clinic-cost-savings': {
    key: 'htm-opportunity-costs-ch-clinic-cost-savings',
    displayName: 'Kosteneinsparung',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/kosteneinsparung',
  },
  'htm-opportunity-costs-ch-clinic-opportunity-costs': {
    key: 'htm-opportunity-costs-ch-clinic-opportunity-costs',
    displayName: 'Opportunitätskosten',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/opportunitaetskosten',
  },
  'htm-opportunity-costs-ch-clinic-parameters': {
    key: 'htm-opportunity-costs-ch-clinic-parameters',
    displayName: 'Parameter',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/parameter',
  },
  'htm-opportunity-costs-ch-clinic-parameters-sonstige-sepsis': {
    key: 'htm-opportunity-costs-ch-clinic-parameters-sonstige-sepsis',
    displayName: 'Sonstige Sepsis',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/parameter#sonstige-sepsis',
  },
  'htm-opportunity-costs-ch-clinic-parameters-infektion-nach-eingriff': {
    key: 'htm-opportunity-costs-ch-clinic-parameters-infektion-nach-eingriff',
    displayName: 'Infektion nach einem Eingriff',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/parameter#infektion-nach-eingriff',
  },
  'htm-opportunity-costs-ch-clinic-parameters-clostridium-difficile': {
    key: 'htm-opportunity-costs-ch-clinic-parameters-clostridium-difficile',
    displayName: 'Enterokolitis durch Clostridioides difficile',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/parameter#clostridium-difficile',
  },
  'htm-opportunity-costs-ch-clinic-parameters-harnwegsinfektion': {
    key: 'htm-opportunity-costs-ch-clinic-parameters-harnwegsinfektion',
    displayName: 'Harnwegsinfektion',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/parameter#harnwegsinfektion',
  },
  'htm-opportunity-costs-ch-clinic-parameters-infektion-untere-atemwege': {
    key: 'htm-opportunity-costs-ch-clinic-parameters-infektion-untere-atemwege',
    displayName: 'Akute Infektion der unteren Atemwege',
    link: 'https://hartmann-help.bindoc.ch/dokumentation/infection-prevention/parameter#infektion-untere-atemwege',
  },
};
